import { useCallback, useContext, useMemo } from 'react';
import { QualificationQuestionType } from 'models/DealRoom';
import { uuid4 } from '@sentry/utils';
import { useTranslation } from 'react-i18next';
import { getSellingPortfolio } from 'utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ListingContext from '../../../providers/Listing/Listing.context';
import confirm from 'modules/confirm';
import { useNavigate } from 'react-router-dom';
import api from 'api';

const AT_LEAST_NUM_OF_ANSWERS = 2;

export default function useQualificationQuestionsForm() {
  const {
    dealRoom: { _id, enabledQualificationQuestions, qualificationQuestions },
    setDealRoom,
  } = useContext(ListingContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const questionsWithOrder = useMemo(() => {
    return qualificationQuestions
      .sort((a, b) => (a.order > b.order ? 1 : -1))
      .map((q) => ({
        ...q,
        id: uuid4(),
        answers: q.answers
          .sort((a, b) => (a.order > b.order ? 1 : -1))
          .map((q) => ({ ...q, id: uuid4() })),
      }));
  }, [qualificationQuestions]);

  const initialValues = useMemo(
    () => ({
      enabledQualificationQuestions,
      qualificationQuestions: questionsWithOrder,
    }),
    [enabledQualificationQuestions, questionsWithOrder],
  );

  const onSubmit = useCallback(
    async (values: {
      enabledQualificationQuestions: boolean;
      qualificationQuestions: QualificationQuestionType[];
    }) => {
      const { qualificationQuestions, enabledQualificationQuestions } =
        values || {};

      let finalQualificationQuestions = qualificationQuestions || [];

      const questionsWithNotEnoughAnswers = finalQualificationQuestions.filter(
        (q) => q.answers.length < AT_LEAST_NUM_OF_ANSWERS,
      );

      if (questionsWithNotEnoughAnswers.length) {
        const titles = questionsWithNotEnoughAnswers.map(({ title }) => title);

        const isSingle = titles.length === 1;

        const titleCopy = isSingle
          ? titles[0]
          : titles.map((title) => `"${title}"`).join(', ');

        if (
          await confirm(
            {
              title: t('questionWithoutEnoughAnswerOptions'),
              submitBtnText: t('confirmRemovalAndProceed'),
              cancelBtnText: t('noGoBack'),
              content: t(
                isSingle
                  ? 'questionWithoutEnoughAnswerOptionsDescription'
                  : 'questionsWithoutEnoughAnswerOptionsDescription',
                {
                  title: titleCopy,
                },
              ),
            },
            true,
          )
        ) {
          finalQualificationQuestions = finalQualificationQuestions.filter(
            (q) => !titles.includes(q.title),
          );
        } else {
          return;
        }
      }

      finalQualificationQuestions = finalQualificationQuestions.map(
        ({ id, autoFocus, ...rest }, index) => ({
          ...rest,
          order: index,
          answers: rest.answers.map(({ id, autoFocus, ...rest }, index) => ({
            ...rest,
            order: index,
          })),
        }),
      );

      try {
        const { data } = await api.dealRoom.updateDealRoom(_id, {
          enabledQualificationQuestions,
          qualificationQuestions: finalQualificationQuestions,
        });
        setDealRoom((prev) => ({ ...prev, ...data }));
        navigate(getSellingPortfolio(_id, 'deal'));
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, navigate, popServerError, setDealRoom, t],
  );

  const onCancel = useCallback(
    async (submit: () => void) => {
      if (
        await confirm(
          {
            title: t('leavingWithoutSavingChanges'),
            content: t('areYouSureYouWantToLeaveWithoutSaving'),
            submitBtnText: t('saveChangesAndLeave'),
            cancelBtnText: t('leaveWithoutSaving'),
          },
          true,
        )
      ) {
        submit();
      } else {
        navigate(getSellingPortfolio(_id, 'deal'));
      }
    },
    [_id, navigate, t],
  );

  return { initialValues, questionsWithOrder, onCancel, onSubmit };
}
